import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Wallet, Loader2, Trash2, ArrowDownCircle, Receipt, X, Plus, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

function dataBR(s?: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('pt-BR')
}

export default function CreditoClientesPage() {
  const qc = useQueryClient()
  const [sel, setSel] = useState<{ pessoa_id: number; nome: string } | null>(null)
  const [lancarOpen, setLancarOpen] = useState(false)

  const { data: clientes = [], isLoading } = useQuery<any[]>({
    queryKey: ['fin-creditos'],
    queryFn: async () => (await api.get('/financeiro/creditos')).data,
  })
  const totalGeral = clientes.reduce((s, c) => s + Number(c.saldo || 0), 0)

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Wallet className="text-brand-600" size={22} />
          <h1 className="text-xl font-bold text-ink-900">Crédito de Clientes</h1>
        </div>
        <button onClick={() => setLancarOpen(true)}
          className="btn-primary flex items-center gap-1.5 !py-2">
          <Plus size={16} /> Incluir crédito
        </button>
      </div>
      <p className="text-sm text-ink-500 mb-5">
        Saldos de haver gerados em devoluções ou lançados manualmente. Use pra abater um título, dar baixa ou pagar no PDV (forma "Crédito cliente").
      </p>

      <div className="bg-white rounded-xl border border-ink-200 p-4 mb-5 inline-block">
        <div className="text-xs text-ink-500 uppercase tracking-wide">Crédito total em aberto</div>
        <div className="text-2xl font-bold text-brand-600">{formatMoney(totalGeral)}</div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : clientes.length === 0 ? (
        <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">Nenhum cliente com crédito.</div>
      ) : (
        <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-right px-4 py-2">Saldo</th>
                <th className="px-4 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {clientes.map(c => (
                <tr key={c.pessoa_id} className="border-t border-ink-100 hover:bg-ink-50">
                  <td className="px-4 py-2 font-medium">{c.nome}</td>
                  <td className="px-4 py-2 text-right font-bold text-brand-600">{formatMoney(c.saldo)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setSel({ pessoa_id: c.pessoa_id, nome: c.nome })}
                      className="btn-secondary !py-1 !px-2 text-xs">Gerenciar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <ModalGerenciarCredito
          pessoaId={sel.pessoa_id} nome={sel.nome}
          onClose={() => setSel(null)}
          onMudou={() => { qc.invalidateQueries({ queryKey: ['fin-creditos'] }) }}
        />
      )}

      {lancarOpen && (
        <ModalLancarCredito
          onClose={() => setLancarOpen(false)}
          onLancou={() => { qc.invalidateQueries({ queryKey: ['fin-creditos'] }); setLancarOpen(false) }}
        />
      )}
    </div>
  )
}

function ModalGerenciarCredito({ pessoaId, nome, onClose, onMudou }: {
  pessoaId: number; nome: string; onClose: () => void; onMudou: () => void
}) {
  const qc = useQueryClient()
  const [aba, setAba] = useState<'extrato' | 'abater' | 'baixa'>('extrato')

  const { data, isLoading } = useQuery<any>({
    queryKey: ['credito-detalhe', pessoaId],
    queryFn: async () => (await api.get(`/financeiro/creditos/${pessoaId}`)).data,
  })
  const saldo = Number(data?.saldo || 0)

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['credito-detalhe', pessoaId] })
    qc.invalidateQueries({ queryKey: ['fin-receber'] })
    onMudou()
  }

  const excluirMov = useMutation({
    mutationFn: (movId: number) => api.delete(`/financeiro/creditos/movimento/${movId}`),
    onSuccess: () => { toast.success('Movimento excluído'); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-semibold text-ink-900">{nome}</h3>
            <div className="text-sm text-ink-500">Saldo de crédito: <b className="text-brand-600">{formatMoney(saldo)}</b></div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={20} /></button>
        </div>

        <div className="flex border-b text-sm">
          {([['extrato', 'Extrato', Receipt], ['abater', 'Abater título', ArrowDownCircle], ['baixa', 'Dar baixa', Trash2]] as const).map(([k, label, Icon]) => (
            <button key={k} onClick={() => setAba(k)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 ${aba === k ? 'border-b-2 border-brand-600 text-brand-700 font-medium' : 'text-ink-500'}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-ink-400" /></div>
          ) : aba === 'extrato' ? (
            <div className="space-y-1.5">
              {(data?.movimentos || []).length === 0 && <div className="text-ink-400 text-sm text-center py-4">Sem movimentos.</div>}
              {(data?.movimentos || []).map((m: any) => (
                <div key={m.id} className="flex items-center justify-between text-sm border border-ink-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="font-medium">
                      <span className={m.tipo === 'entrada' ? 'text-emerald-600' : 'text-rose-600'}>
                        {m.tipo === 'entrada' ? '+' : '−'} {formatMoney(m.valor)}
                      </span>
                      <span className="text-ink-400 text-xs ml-2">{m.origem}</span>
                    </div>
                    <div className="text-xs text-ink-500">{m.descricao} · {dataBR(m.criado_em)}</div>
                  </div>
                  <button onClick={() => pedirExclusao({ descricao: `este movimento de crédito (${m.tipo === 'entrada' ? '+' : '−'} ${formatMoney(m.valor)}) — o saldo será recalculado`, onConfirm: () => excluirMov.mutate(m.id) })}
                    className="text-ink-400 hover:text-rose-600" title="Excluir movimento">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          ) : aba === 'abater' ? (
            <AbaterTitulo pessoaId={pessoaId} saldo={saldo} onFeito={invalidar} />
          ) : (
            <DarBaixa pessoaId={pessoaId} saldo={saldo} onFeito={invalidar} />
          )}
        </div>
      </div>
    </div>
  )
}

function AbaterTitulo({ pessoaId, saldo, onFeito }: { pessoaId: number; saldo: number; onFeito: () => void }) {
  const { data: contas = [], isLoading } = useQuery<any[]>({
    queryKey: ['receber-aberto', pessoaId],
    queryFn: async () => {
      const todas = (await api.get('/financeiro', { params: { tipo: 'receber', status: 'aberto' } })).data as any[]
      return todas.filter(c => c.pessoa_id === pessoaId)
    },
  })
  const abater = useMutation({
    mutationFn: (conta_id: number) => api.post(`/financeiro/creditos/${pessoaId}/abater-titulo`, { conta_id }),
    onSuccess: (r: any) => { toast.success(`Abatido ${formatMoney(r?.data?.abatido)} no título`); onFeito() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  if (saldo <= 0) return <div className="text-ink-400 text-sm text-center py-4">Cliente sem saldo de crédito.</div>
  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="animate-spin text-ink-400" /></div>
  if (contas.length === 0) return <div className="text-ink-400 text-sm text-center py-4">Nenhuma conta a receber em aberto deste cliente.</div>

  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-500">Escolha o título a abater com o crédito (até {formatMoney(saldo)}):</p>
      {contas.map(c => {
        const emAberto = Number(c.valor || 0) - Number(c.valor_pago || 0)
        return (
          <div key={c.id} className="flex items-center justify-between border border-ink-100 rounded-lg px-3 py-2 text-sm">
            <div>
              <div className="font-medium">{c.descricao}</div>
              <div className="text-xs text-ink-500">Vence {dataBR(c.vencimento)} · em aberto {formatMoney(emAberto)}</div>
            </div>
            <button onClick={() => abater.mutate(c.id)} disabled={abater.isPending}
              className="btn-primary !py-1 !px-3 text-xs">Abater</button>
          </div>
        )
      })}
    </div>
  )
}

function DarBaixa({ pessoaId, saldo, onFeito }: { pessoaId: number; saldo: number; onFeito: () => void }) {
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const baixa = useMutation({
    mutationFn: () => api.post(`/financeiro/creditos/${pessoaId}/baixa`, {
      valor: valor ? Number(valor) : undefined, motivo: motivo || undefined,
    }),
    onSuccess: (r: any) => { toast.success(`Baixa de ${formatMoney(r?.data?.baixado)}`); onFeito(); setValor(''); setMotivo('') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  if (saldo <= 0) return <div className="text-ink-400 text-sm text-center py-4">Cliente sem saldo de crédito.</div>

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">Baixa do crédito (write-off) — ex.: cliente recebeu o valor em dinheiro. Deixe o valor vazio pra baixar o saldo todo ({formatMoney(saldo)}).</p>
      <div>
        <label className="text-xs font-semibold text-ink-500 uppercase">Valor (opcional)</label>
        <input type="number" step="0.01" min="0" max={saldo} value={valor} onChange={e => setValor(e.target.value)}
          placeholder={formatMoney(saldo)} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-500 uppercase">Motivo</label>
        <input value={motivo} onChange={e => setMotivo(e.target.value.toUpperCase())}
          placeholder="Ex.: devolvido em dinheiro" className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
      </div>
      <button onClick={() => baixa.mutate()} disabled={baixa.isPending}
        className="w-full btn-primary !bg-rose-600 hover:!bg-rose-700">
        {baixa.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Dar baixa no crédito'}
      </button>
    </div>
  )
}

function ModalLancarCredito({ onClose, onLancou }: { onClose: () => void; onLancou: () => void }) {
  const [cliente, setCliente] = useState<{ id: number; nome: string } | null>(null)
  const [busca, setBusca] = useState('')
  const [abrir, setAbrir] = useState(false)
  const [valor, setValor] = useState('')
  const [descricao, setDescricao] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbrir(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Busca de cliente server-side (lista grande).
  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['credito-busca-cliente', busca],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: 'cliente', q: busca.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrir,
  })

  const lancar = useMutation({
    mutationFn: () => api.post('/financeiro/creditos/lancar', {
      pessoa_id: cliente!.id, valor: Number(valor), descricao: descricao || undefined,
    }),
    onSuccess: (r: any) => { toast.success(`Crédito de ${formatMoney(r?.data?.lancado)} lançado`); onLancou() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao lançar crédito'),
  })

  const podeSalvar = cliente && Number(valor) > 0
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><Plus size={18} /> Incluir crédito</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <div className="relative" ref={ref}>
            <label className="text-xs font-semibold text-ink-500 uppercase">Cliente</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                className="w-full rounded-lg border border-ink-200 pl-9 pr-3 py-2 text-sm"
                placeholder="Digite o nome do cliente..."
                value={busca}
                autoComplete="off"
                onChange={e => { setBusca(e.target.value); setAbrir(true); if (cliente) setCliente(null) }}
                onFocus={() => setAbrir(true)}
              />
            </div>
            {abrir && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                {clientes.length === 0 ? (
                  <div className="p-3 text-center text-sm text-ink-400">Nenhum cliente encontrado</div>
                ) : (
                  clientes.map((c: any) => (
                    <button key={c.id} type="button"
                      onClick={() => { setCliente({ id: c.id, nome: c.nome }); setBusca(c.nome); setAbrir(false) }}
                      className="w-full px-3 py-2 text-left border-b last:border-0 hover:bg-ink-50">
                      <p className="text-sm font-medium truncate">{c.nome}</p>
                      {(c.cpf_cnpj || c.telefone) && <span className="text-xs text-ink-500">{c.cpf_cnpj || c.telefone}</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Valor do crédito</label>
            <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)}
              placeholder="0,00" className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Motivo / observação</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value.toUpperCase())}
              placeholder="Ex.: acerto, cortesia, vale" className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>

          <button onClick={() => lancar.mutate()} disabled={!podeSalvar || lancar.isPending}
            className="w-full btn-primary disabled:opacity-50">
            {lancar.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Lançar crédito'}
          </button>
        </div>
      </div>
    </div>
  )
}
